import { useEffect, useState } from "react";
import { Layers, Lock, Pencil, Plus, RefreshCw, Trash2, Users2 } from "lucide-react";
import { PageHeader } from "@/components/app-layout";
import { LoadMore } from "@/components/load-more";
import { Button } from "@/components/ui/button";
import { Card, CardBody, CardHeader } from "@/components/ui/card";
import { Alert } from "@/components/ui/alert";
import { ErrorAlert } from "@/components/error-alert";
import { AccountSelector } from "@/components/account-selector";
import { GroupSetEditor } from "@/components/group-set-editor";
import { useAccounts } from "@/lib/accounts";
import { useDeleteGroupSet, useGroupSets, useGroups, useSyncGroups, type GroupSet } from "@/lib/groups";

export function GroupsPage() {
    const { data: accounts } = useAccounts();
    const [accountId, setAccountId] = useState<string>();
    const [editor, setEditor] = useState<{ open: boolean; editing: GroupSet | null }>({ open: false, editing: null });

    useEffect(() => {
        const first = accounts?.[0];
        if (!accountId && first) setAccountId(first.id);
    }, [accounts, accountId]);

    const groupQuery = useGroups(accountId);
    const groups = groupQuery.items;
    const groupsPending = groupQuery.isPending;
    const { data: groupSets } = useGroupSets(accountId);
    const sync = useSyncGroups();
    const removeSet = useDeleteGroupSet();

    const lockedCount = groups.filter((group) => group.isSendLocked).length;

    if (!accounts?.length) {
        return (
            <>
                <PageHeader title="Nhóm" description="Đồng bộ nhóm từ Zalo và gom thành nhóm gửi" />
                <div className="p-8">
                    <Alert tone="info">Chưa có tài khoản Zalo nào. Thêm tài khoản trước rồi quay lại đây.</Alert>
                </div>
            </>
        );
    }

    return (
        <>
            <PageHeader
                title="Nhóm"
                description="Đồng bộ nhóm từ Zalo và gom thành nhóm gửi dùng lại được"
                action={
                    <Button
                        variant="secondary"
                        loading={sync.isPending}
                        onClick={() => accountId && sync.mutate(accountId)}
                    >
                        <RefreshCw className="size-4" />
                        Đồng bộ nhóm
                    </Button>
                }
            />

            <div className="space-y-4 p-8">
                <AccountSelector accounts={accounts} value={accountId} onChange={setAccountId} />

                <ErrorAlert error={sync.error} context="Đồng bộ nhóm thất bại" />
                {sync.isSuccess && !sync.isPending && (
                    <Alert tone="info">
                        Đã đồng bộ {sync.data.total} nhóm — thêm mới {sync.data.created}, cập nhật {sync.data.updated}
                        {sync.data.left > 0 && `, đánh dấu đã rời ${sync.data.left}`}.
                    </Alert>
                )}

                <Card>
                    <CardHeader
                        title="Nhóm gửi"
                        description="Gom sẵn các nhóm hay gửi cùng nhau để chọn nhanh khi tạo chiến dịch"
                        action={
                            <Button size="sm" onClick={() => setEditor({ open: true, editing: null })}>
                                <Plus className="size-4" />
                                Tạo nhóm gửi
                            </Button>
                        }
                    />
                    <CardBody className="space-y-2">
                        {groupSets?.length === 0 && (
                            <p className="py-6 text-center text-sm text-ink-muted">
                                Chưa có nhóm gửi nào. Tạo một cái để chọn nhanh khi soạn chiến dịch.
                            </p>
                        )}

                        {groupSets?.map((set) => (
                            <div
                                key={set.id}
                                className="flex items-center justify-between gap-3 rounded-lg border border-line px-3 py-2.5"
                            >
                                <div className="flex min-w-0 items-center gap-2.5">
                                    <Layers className="size-4 shrink-0 text-ink-muted" />
                                    <div className="min-w-0">
                                        <p className="truncate text-sm font-medium text-ink">{set.name}</p>
                                        <p className="text-xs text-ink-muted">
                                            {set.sendableCount}/{set.groupIds.length} nhóm gửi được
                                        </p>
                                    </div>
                                </div>
                                <div className="flex shrink-0 gap-1.5">
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => setEditor({ open: true, editing: set })}
                                    >
                                        <Pencil className="size-4" />
                                    </Button>
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => confirm(`Xoá nhóm gửi "${set.name}"?`) && removeSet.mutate(set.id)}
                                    >
                                        <Trash2 className="size-4 text-danger" />
                                    </Button>
                                </div>
                            </div>
                        ))}
                    </CardBody>
                </Card>

                <Card>
                    <CardHeader
                        title={`Tất cả nhóm (${groupQuery.total})`}
                        description={
                            lockedCount > 0
                                ? `${lockedCount} nhóm bị khoá gửi — chiến dịch sẽ tự bỏ qua`
                                : "Danh sách nhóm đồng bộ từ Zalo"
                        }
                    />
                    <CardBody className="space-y-1">
                        {groupsPending && <p className="py-6 text-center text-sm text-ink-muted">Đang tải…</p>}
                        {groups.length === 0 && (
                            <p className="py-6 text-center text-sm text-ink-muted">
                                Chưa có nhóm nào. Bấm “Đồng bộ nhóm” để lấy từ Zalo.
                            </p>
                        )}

                        {groups.map((group) => (
                            <div key={group.id} className="flex items-center gap-3 rounded-lg px-2 py-2 hover:bg-surface-muted">
                                {group.avatarUrl ? (
                                    <img src={group.avatarUrl} alt="" className="size-8 shrink-0 rounded-full" />
                                ) : (
                                    <div className="size-8 shrink-0 rounded-full bg-surface-muted" />
                                )}
                                <span className="min-w-0 flex-1 truncate text-sm text-ink">{group.name}</span>
                                {group.isSendLocked && (
                                    <span className="flex items-center gap-1 text-xs text-warning">
                                        <Lock className="size-3" />
                                        khoá gửi
                                    </span>
                                )}
                                <span className="flex items-center gap-1 text-xs text-ink-muted">
                                    <Users2 className="size-3.5" />
                                    {group.memberCount}
                                </span>
                            </div>
                        ))}

                        <LoadMore
                            shown={groups.length}
                            total={groupQuery.total}
                            hasMore={groupQuery.hasMore}
                            loading={groupQuery.isLoadingMore}
                            onLoadMore={groupQuery.loadMore}
                            unit="nhóm"
                        />
                    </CardBody>
                </Card>
            </div>

            {editor.open && accountId && (
                <GroupSetEditor
                    accountId={accountId}
                    editing={editor.editing}
                    onClose={() => setEditor({ open: false, editing: null })}
                />
            )}
        </>
    );
}
